import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    IconButton,
    Divider,
    Stack
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Order, OrderInvoice } from '../../../app/models/order';
import { toast } from 'react-toastify';
import agent from '../../../app/api/agent';
import { useAppDispatch } from '../../../app/store/configureStore';
import { setAdminOrder } from './adminOrdersSlice';

interface Props {
    open: boolean;
    onClose: () => void;
    order: Order;
}

const BIZIN_BASE_URL = "https://system.bizin.pl/invoice.html?type=pdf&";

export default function AdminOrderInvoiceUrlDialog({ open, onClose, order }: Props) {
    const [url, setUrl] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | undefined>(undefined);
    const [invoiceToDelete, setInvoiceToDelete] = useState<Pick<OrderInvoice, 'id' | 'accountNumber' | 'bizInInvoiceURL'> | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const dispatch = useAppDispatch();

    const invoices: Pick<OrderInvoice, 'id' | 'accountNumber' | 'bizInInvoiceURL'>[] = order.invoices?.length
        ? order.invoices
        : order.bizInInvoiceURL
            ? [{
                id: 0,
                accountNumber: order.accountNumber,
                bizInInvoiceURL: order.bizInInvoiceURL
            }]
            : [];

    const selectedInvoice = selectedInvoiceId
        ? invoices.find(invoice => invoice.id === selectedInvoiceId)
        : undefined;

    useEffect(() => {
        if (open) {
            startAddingInvoice();
        }
    }, [open]);

    const startAddingInvoice = () => {
        setSelectedInvoiceId(undefined);
        setUrl('');
        setAccountNumber('');
    };

    const startEditingInvoice = (invoice: Pick<OrderInvoice, 'id' | 'accountNumber' | 'bizInInvoiceURL'>) => {
        setSelectedInvoiceId(invoice.id || undefined);
        setUrl(invoice.bizInInvoiceURL || '');
        setAccountNumber(invoice.accountNumber || '');
    };

    const handleClose = () => {
        startAddingInvoice();
        setInvoiceToDelete(null);
        onClose();
    };

    const handleDeleteInvoice = async () => {
        if (!invoiceToDelete?.id) return;

        setIsDeleting(true);
        try {
            const updatedOrder = await agent.Admin.Orders.deleteInvoice(order.publicId, invoiceToDelete.id);
            toast.success('Faktura została usunięta');
            dispatch(setAdminOrder(updatedOrder));
            if (selectedInvoiceId === invoiceToDelete.id) {
                startAddingInvoice();
            }
            setInvoiceToDelete(null);
        } catch (error: any) {
            console.error('Error deleting invoice:', error);
            if (error.response?.data?.title) {
                toast.error(error.response.data.title);
            } else {
                toast.error('Wystąpił błąd podczas usuwania faktury');
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const validateAccountNumber = (value: string): boolean => {
        const regex = /^\d{3}\/\d{2}\/\d{4}$/;
        return regex.test(value);
    };

    const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        const cleanInput = input.replace(/[^\d/]/g, '');
        const digitsOnly = cleanInput.replace(/\D/g, '');

        if (digitsOnly.length > 9) {
            return;
        }

        let formatted = '';

        if (digitsOnly.length > 0) {
            formatted = digitsOnly.substring(0, 3);

            if (digitsOnly.length > 3) {
                formatted += '/' + digitsOnly.substring(3, 5);

                if (digitsOnly.length > 5) {
                    formatted += '/' + digitsOnly.substring(5, 9);
                }
            }
        }

        if (cleanInput.length <= 11 &&
            cleanInput.match(/^\d{0,3}\/?(\d{0,2})?\/?(\d{0,4})?$/)) {
            setAccountNumber(cleanInput);
        } else {
            setAccountNumber(formatted);
        }
    };

    const extractParameters = (inputUrl: string): string | null => {
        try {
            if (inputUrl.includes('idprint=') || inputUrl.includes('inst=') || inputUrl.includes('klucz=')) {
                if (inputUrl.includes(BIZIN_BASE_URL)) {
                    const urlObj = new URL(inputUrl);
                    const params = new URLSearchParams(urlObj.search);

                    const idprint = params.get('idprint');
                    const inst = params.get('inst');
                    const klucz = params.get('klucz');
                    const tExt = params.get('tExt') || 'regular';

                    if (idprint && inst && klucz) {
                        return `idprint=${idprint}&inst=${inst}&klucz=${klucz}&tExt=${tExt}`;
                    }
                } else if (!inputUrl.startsWith('http')) {
                    let cleanParams = inputUrl.replace(/^[?&]/, '');

                    const hasIdprint = cleanParams.includes('idprint=');
                    const hasInst = cleanParams.includes('inst=');
                    const hasKlucz = cleanParams.includes('klucz=');

                    if (hasIdprint && hasInst && hasKlucz) {
                        if (!cleanParams.includes('tExt=')) {
                            cleanParams += '&tExt=regular';
                        }
                        return cleanParams;
                    }
                }
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    const handleSubmit = async () => {
        if (!url.trim()) {
            toast.error('Wprowadź URL faktury lub parametry');
            return;
        }

        if (!accountNumber.trim()) {
            toast.error('Wprowadź numer faktury');
            return;
        }

        if (!validateAccountNumber(accountNumber)) {
            toast.error('Numer faktury musi mieć format: XXX/XX/XXXX (np. 029/06/2025)');
            return;
        }

        let finalUrl = '';

        if (url.startsWith(BIZIN_BASE_URL)) {
            finalUrl = url;
        } else {
            const parameters = extractParameters(url);
            if (parameters) {
                finalUrl = BIZIN_BASE_URL + parameters;
            } else {
                toast.error('Nieprawidłowy format URL. Wprowadź kompletny URL BizIn lub parametry (idprint, inst, klucz)');
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const updatedOrder = await agent.Admin.Orders.updateInvoiceUrl(
                order.publicId,
                finalUrl,
                accountNumber,
                selectedInvoiceId
            );
            toast.success(selectedInvoiceId
                ? 'Dane faktury zostały zaktualizowane'
                : 'Faktura została dodana');
            dispatch(setAdminOrder(updatedOrder));
            handleClose();
        } catch (error: any) {
            console.error('Error updating invoice URL:', error);
            if (error.response?.data?.title) {
                toast.error(error.response.data.title);
            } else {
                toast.error('Wystąpił błąd podczas zapisywania danych faktury');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const isValidUrl = url.trim() && (
        url.startsWith(BIZIN_BASE_URL) ||
        extractParameters(url) !== null
    );

    const isValidAccountNumber = accountNumber.trim() && validateAccountNumber(accountNumber);
    const formTitle = selectedInvoiceId
        ? `Edytuj fakturę nr ${selectedInvoice?.accountNumber || accountNumber || selectedInvoiceId}`
        : 'Dodaj fakturę';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '400px' }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon color="primary" />
                    <Typography variant="h6">
                        Dane faktury BizIn
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Zamówienie: <strong>#{order.publicId}</strong> - {order.customeClientServiceName}
                </Typography>

                {invoices.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <ReceiptIcon fontSize="small" color="primary" />
                            Istniejące faktury
                        </Typography>
                        <Stack spacing={1}>
                            {invoices.map((invoice) => (
                                <Box
                                    key={invoice.id || invoice.bizInInvoiceURL}
                                    sx={{
                                        display: 'flex',
                                        alignItems: { xs: 'stretch', sm: 'center' },
                                        justifyContent: 'space-between',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: 1,
                                        p: 1.5,
                                        border: 1,
                                        borderColor: selectedInvoiceId === invoice.id ? 'primary.main' : 'divider',
                                        borderRadius: 1
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle2">
                                            Faktura nr {invoice.accountNumber || 'bez numeru'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                            {invoice.bizInInvoiceURL}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<OpenInNewIcon />}
                                            onClick={() => window.open(invoice.bizInInvoiceURL, '_blank')}
                                        >
                                            Otwórz
                                        </Button>
                                        {!!invoice.id && (
                                            <>
                                                <Button
                                                    variant={selectedInvoiceId === invoice.id ? 'contained' : 'outlined'}
                                                    size="small"
                                                    color="warning"
                                                    startIcon={<EditIcon />}
                                                    onClick={() => startEditingInvoice(invoice)}
                                                >
                                                    Edytuj
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="error"
                                                    startIcon={<DeleteIcon />}
                                                    onClick={() => setInvoiceToDelete(invoice)}
                                                >
                                                    Usuń
                                                </Button>
                                            </>
                                        )}
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                <Button
                    variant={selectedInvoiceId ? 'outlined' : 'contained'}
                    startIcon={<AddIcon />}
                    onClick={startAddingInvoice}
                    sx={{ mb: 3 }}
                >
                    Dodaj kolejną fakturę
                </Button>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="h6" sx={{ mb: 2 }}>
                    {formTitle}
                </Typography>

                <TextField
                    fullWidth
                    label="Numer faktury"
                    placeholder="029/06/2025"
                    value={accountNumber}
                    onChange={handleAccountNumberChange}
                    sx={{ mb: 3 }}
                    helperText="Format: XXX/XX/XXXX (3 cyfry / 2 cyfry / 4 cyfry)"
                    inputProps={{
                        maxLength: 11
                    }}
                />

                {accountNumber && !validateAccountNumber(accountNumber) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Nieprawidłowy format numeru faktury. Użyj formatu: XXX/XX/XXXX
                        </Typography>
                    </Alert>
                )}

                <TextField
                    fullWidth
                    label="URL faktury BizIn"
                    placeholder="Wklej kompletny URL lub parametry (idprint=343&inst=11314&klucz=...)"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    multiline
                    rows={4}
                    sx={{ mb: 3 }}
                    helperText={
                        <>
                            <strong>Formaty akceptowane:</strong><br />
                            • Kompletny URL: https://system.bizin.pl/invoice.html?type=pdf&idprint=343&inst=11314&klucz=...<br />
                            • Tylko parametry: idprint=343&inst=11314&klucz=...&tExt=regular
                        </>
                    }
                />

                {url && isValidUrl && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Podgląd URL:</strong><br />
                            {url.startsWith(BIZIN_BASE_URL) ? url : BIZIN_BASE_URL + extractParameters(url)}
                        </Typography>
                    </Alert>
                )}

                {url && !isValidUrl && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Nieprawidłowy format. Upewnij się, że URL zawiera parametry: idprint, inst i klucz.
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button onClick={handleClose} variant="outlined">
                    Anuluj
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={!isValidUrl || !isValidAccountNumber}
                    variant="contained"
                    color="primary"
                >
                    {selectedInvoiceId ? 'Zapisz zmiany faktury' : 'Dodaj fakturę'}
                </LoadingButton>
            </DialogActions>

            <Dialog open={!!invoiceToDelete} onClose={() => setInvoiceToDelete(null)}>
                <DialogTitle>Usuń fakturę</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Czy na pewno chcesz usunąć fakturę nr {invoiceToDelete?.accountNumber || 'bez numeru'}?
                        Rekord zostanie zachowany w historii, ale przestanie być aktywny.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInvoiceToDelete(null)} disabled={isDeleting}>
                        Anuluj
                    </Button>
                    <LoadingButton
                        color="error"
                        variant="contained"
                        loading={isDeleting}
                        onClick={handleDeleteInvoice}
                    >
                        Usuń fakturę
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
